import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ArrowDown, ArrowUp, Eye, EyeOff, Plus, Video } from 'lucide-react';
import {
  Badge,
  Button,
  DataTable,
  DeleteConfirmDialog,
  createActionsColumn,
  createColumnHeader,
  type DataTableColumnDef
} from '@bliks-io/ui';
import { PARTNER_MATERIAL_LOCALES, type PartnerMaterialAdmin } from '@bliks-io/contracts';
import { useAdminPartnerMaterials } from '@/pages/admin/api/queries/usePartnerMaterials';
import {
  useDeletePartnerMaterial,
  useUpdatePartnerMaterial
} from '@/pages/admin/api/mutations/usePartnerMaterials';
import { PartnerFileFormatIcon } from '@/components/partner-content/PartnerFileFormat';
import {
  PARTNER_MATERIAL_CATEGORY_KEYS,
  PARTNER_MATERIAL_LOCALE_NAMES
} from '@/lib/partner-material-text';
import { formatDate } from '@/lib/formatters';
import { PublishStatusBadge } from '@/pages/admin/partner-content/components/PublishStatusBadge';
import { usePublishFilter } from '@/pages/admin/partner-content/usePublishFilter';
import { MaterialDialog } from './MaterialDialog';
import { MATERIAL_KIND_KEYS } from './materialForm';
import { reorderWithinCategory } from './reorderWithinCategory';

type MaterialRow = PartnerMaterialAdmin & { categoryLabel: string };

export function MaterialsTab() {
  const { t } = useTranslation();
  const { data: materials = [], isLoading, isError } = useAdminPartnerMaterials();
  const updateMaterial = useUpdatePartnerMaterial();
  const deleteMaterial = useDeletePartnerMaterial();
  const { matches, quickFilter } = usePublishFilter();

  // Each opening gets its own key, so the dialog never shows a previous draft.
  const [editor, setEditor] = useState<{
    session: number;
    material: PartnerMaterialAdmin | null;
  }>();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState<PartnerMaterialAdmin | null>(null);

  const openEditor = (material: PartnerMaterialAdmin | null) => {
    setEditor((current) => ({ session: (current?.session ?? 0) + 1, material }));
    setDialogOpen(true);
  };
  const showError = () => toast.error(t('common.Error'));

  const move = (material: PartnerMaterialAdmin, direction: -1 | 1) => {
    for (const { id, position } of reorderWithinCategory(materials, material._id, direction)) {
      updateMaterial.mutate({ id, data: { position } }, { onError: showError });
    }
  };
  const setPublished = (material: PartnerMaterialAdmin, active: boolean) => {
    const hasFile = Object.keys(material.files).length > 0;
    if (active && material.kind === 'file' && !hasFile) {
      toast.error(t('partnerContent.materials.needsFile'));
      return;
    }
    updateMaterial.mutate({ id: material._id, data: { active } }, { onError: showError });
  };

  // The category label is row data, not an accessor: accessors stay pure,
  // and the table groups by it.
  const rows = useMemo<MaterialRow[]>(
    () =>
      materials
        .filter((material) => matches(material.active))
        .map((material) => ({
          ...material,
          categoryLabel: t(PARTNER_MATERIAL_CATEGORY_KEYS[material.category])
        })),
    [materials, matches, t]
  );

  const columns: DataTableColumnDef<MaterialRow>[] = [
    {
      accessorKey: 'categoryLabel',
      meta: { label: t('partner.infoHub.category') },
      header: createColumnHeader(t('partner.infoHub.category'))
    },
    {
      id: 'order',
      meta: { fitContent: true },
      header: () => <span className="sr-only">{t('partnerContent.columns.order')}</span>,
      cell: ({ row }) => (
        <div className="flex">
          <Button
            variant="ghost"
            size="icon"
            aria-label={t('partnerContent.actions.moveUp')}
            onClick={() => move(row.original, -1)}
          >
            <ArrowUp className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            aria-label={t('partnerContent.actions.moveDown')}
            onClick={() => move(row.original, 1)}
          >
            <ArrowDown className="h-4 w-4" />
          </Button>
        </div>
      )
    },
    {
      id: 'title',
      accessorFn: (material) => material.title.de,
      header: createColumnHeader(t('partnerContent.columns.title')),
      cell: ({ row }) => (
        <MaterialTitleCell material={row.original} onOpen={() => openEditor(row.original)} />
      )
    },
    {
      id: 'status',
      meta: { fitContent: true },
      header: createColumnHeader(t('partnerContent.columns.status')),
      cell: ({ row }) => <PublishStatusBadge published={row.original.active} />
    },
    {
      id: 'languages',
      header: createColumnHeader(t('partnerContent.columns.languages')),
      cell: ({ row }) => <LanguageCoverageCell material={row.original} />
    },
    {
      id: 'updatedAt',
      meta: { fitContent: true },
      header: createColumnHeader(t('partnerContent.columns.updatedAt')),
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-sm text-muted-foreground">
          {formatDate(row.original.updatedAt)}
        </span>
      )
    },
    createActionsColumn<MaterialRow>({
      onEdit: (material) => openEditor(material),
      onDelete: (material) => setDeleting(material),
      getCustomActions: (material) => [
        material.active
          ? {
              label: t('partnerContent.actions.unpublish'),
              icon: <EyeOff className="h-4 w-4" />,
              onClick: () => setPublished(material, false)
            }
          : {
              label: t('partnerContent.actions.publish'),
              icon: <Eye className="h-4 w-4" />,
              onClick: () => setPublished(material, true)
            }
      ]
    })
  ];

  if (isError) return <p className="text-destructive">{t('partnerContent.materials.loadError')}</p>;

  return (
    <>
      <DataTable
        columns={columns}
        data={rows}
        isLoading={isLoading}
        enableSorting={false}
        enablePagination={false}
        enableGlobalFilter
        initialGrouping={['categoryLabel']}
        emptyMessage={t(
          materials.length === 0 ? 'partnerContent.materials.empty' : 'partnerContent.emptyFiltered'
        )}
        quickFilters={[quickFilter]}
        toolbarContent={
          <Button onClick={() => openEditor(null)}>
            <Plus className="mr-2 h-4 w-4" />
            {t('partnerContent.materials.add')}
          </Button>
        }
      />

      {editor && (
        <MaterialDialog
          key={editor.session}
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          material={
            editor.material
              ? (materials.find((material) => material._id === editor.material?._id) ??
                editor.material)
              : null
          }
        />
      )}

      <DeleteConfirmDialog
        open={deleting !== null}
        onOpenChange={(open) => !open && setDeleting(null)}
        onConfirm={() => {
          if (!deleting) return;
          deleteMaterial.mutate(deleting._id, {
            onSuccess: () => setDeleting(null),
            onError: showError
          });
        }}
        isDeleting={deleteMaterial.isPending}
        entityPhrase={t('partnerContent.materials.deleteEntityPhrase')}
      />
    </>
  );
}

function MaterialTitleCell({
  material,
  onOpen
}: {
  material: PartnerMaterialAdmin;
  onOpen: () => void;
}) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex min-w-0 items-center gap-3 text-left hover:underline"
    >
      {material.kind === 'video' ? (
        <Video className="h-5 w-5 shrink-0 text-muted-foreground" />
      ) : (
        <PartnerFileFormatIcon format={Object.values(material.files)[0]?.format} />
      )}
      <div className="min-w-0">
        <p className="truncate font-medium">{material.title.de}</p>
        <p className="text-xs text-muted-foreground">
          {t(MATERIAL_KIND_KEYS[material.kind].label)}
        </p>
      </div>
    </button>
  );
}

/**
 * Every language, present or not: the gaps are what an editor needs to see.
 * A file material counts a language once its file exists, a video once its
 * title is translated.
 */
function LanguageCoverageCell({ material }: { material: PartnerMaterialAdmin }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap gap-1">
      {PARTNER_MATERIAL_LOCALES.map((locale) => {
        const present =
          material.kind === 'file'
            ? Boolean(material.files[locale])
            : Boolean(material.title[locale]);
        return (
          <Badge
            key={locale}
            variant="outline"
            shape="rounded"
            size="sm"
            colorScheme={present ? 'success' : 'none'}
            title={t(
              present ? 'partnerContent.languages.present' : 'partnerContent.languages.missing',
              {
                language: PARTNER_MATERIAL_LOCALE_NAMES[locale]
              }
            )}
          >
            <span className={present ? undefined : 'text-muted-foreground line-through'}>
              {locale.toUpperCase()}
            </span>
          </Badge>
        );
      })}
    </div>
  );
}
